/**
 * @file Collider component: an XZ axis-aligned box plus its layer filter.
 */

import { Component } from '../ecs/Component.js';
import { Layer } from '../config/GameConfig.js';

export class Collider extends Component {
  /**
   * @param {object} options Collider setup.
   * @param {number} options.halfX Half-extent along X.
   * @param {number} options.halfZ Half-extent along Z.
   * @param {number} options.layer Layer this collider belongs to.
   * @param {number} options.mask Bitmask of layers it may collide with.
   * @param {boolean} [options.isStatic] Static bodies are never pushed.
   * @param {boolean} [options.isTrigger] Triggers report overlaps but do not resolve them.
   */
  constructor({ halfX, halfZ, layer, mask, isStatic = false, isTrigger = false }) {
    super();
    /** @type {number} */
    this.halfX = halfX;
    /** @type {number} */
    this.halfZ = halfZ;
    /** @type {number} */
    this.layer = layer ?? Layer.WALL;
    /** @type {number} */
    this.mask = mask ?? 0;
    /** @type {boolean} */
    this.isStatic = isStatic;
    /** @type {boolean} */
    this.isTrigger = isTrigger;
    /** @type {boolean} True when the body touched a static surface this frame. */
    this.touchedStatic = false;
  }

  /**
   * Convenience builder for the round-ish bodies the game uses, kept AABB per
   * GDD §12.
   * @param {number} radius Body radius.
   * @param {number} layer Collision layer.
   * @param {number} mask Layers it may collide with.
   * @param {boolean} [isStatic] Whether the body is immovable.
   * @returns {Collider}
   */
  static fromRadius(radius, layer, mask, isStatic = false) {
    return new Collider({ halfX: radius, halfZ: radius, layer, mask, isStatic });
  }

  /** @returns {void} */
  reset() {
    this.touchedStatic = false;
  }
}
